import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BancoldexService } from 'src/app/comun/services/bancoldex.service';
import {
  COOKIE_MODAL_TEXT_PARAM,
  COOKIE_POLICY_URL_PARAM,
} from './cookie-consent.config';
import { normalizeCookieModalParamText } from './cookie-consent-text.util';

export interface CookieConsentModalConfig {
  modalText: string;
  policyUrl: string;
}

@Injectable({
  providedIn: 'root',
})
export class CookieConsentConfigService {
  constructor(private readonly bancoldexService: BancoldexService) {}

  /** Carga PAR-163 y PAR-164 desde backend, sin textos locales de respaldo. */
  loadConfig(): Observable<CookieConsentModalConfig> {
    return forkJoin({
      modalText: this.loadParamValue(COOKIE_MODAL_TEXT_PARAM).pipe(
        map((text) => normalizeCookieModalParamText(text))
      ),
      policyUrl: this.loadParamValue(COOKIE_POLICY_URL_PARAM).pipe(
        map((url) => this.sanitizePolicyUrl(url) ?? '')
      ),
    }).pipe(catchError(() => of({ modalText: '', policyUrl: '' })));
  }

  loadModalConfig(): Observable<CookieConsentModalConfig> {
    return this.loadConfig();
  }

  loadPolicyUrl(): Observable<string> {
    return this.loadParamValue(COOKIE_POLICY_URL_PARAM).pipe(
      map((url) => this.sanitizePolicyUrl(url) ?? ''),
      catchError(() => of(''))
    );
  }

  loadFullConfig(): Observable<CookieConsentModalConfig> {
    return this.loadConfig();
  }

  sanitizePolicyUrl(raw: string | null | undefined): string | null {
    if (!raw?.trim()) {
      return null;
    }
    try {
      const parsed = new URL(raw.trim());
      if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
        return parsed.toString();
      }
    } catch {
      if (raw.startsWith('/')) {
        return raw.trim();
      }
    }
    return null;
  }

  private loadParamValue(idParametro: string): Observable<string> {
    return this.bancoldexService
      .getFilterParametro({
        idParametro,
        nombre: null,
        isParFuncional: true,
        pagina: 1,
      })
      .pipe(
        map(
          (response: { data?: { valorParametro?: string }[] }) =>
            response?.data?.[0]?.valorParametro?.trim() ?? ''
        ),
        catchError(() => of(''))
      );
  }
}
